import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DockerImageSelect from '@tenant/core/App/Select/DockerImageSelect';
import DockerAppTypes from 'constants/DockerAppTypes';
import useAddContainerToInstance from 'redux/slices/appInstance/hooks/useAddContainerToInstance';

const isCronType = (deploymentType) =>
  DockerAppTypes.find((e) => e.Value === deploymentType)?.IsCron ?? false;

// Add (or re-add) a single container to a package-free (inline) instance. Env vars can be
// configured on the container after it is added, via the existing per-container controls.
const AddContainerDialog = ({
  open,
  onClose,
  appId,
  appInstanceId,
  existingNames = [],
  onAdded,
}) => {
  const [name, setName] = useState('');
  const [image, setImage] = useState(null);
  const [cron, setCron] = useState('');
  const addMutation = useAddContainerToInstance();

  const isCron = isCronType(image?.DeploymentType);
  const nameTrimmed = name.trim();
  const isDuplicate = existingNames.some(
    (n) => (n ?? '').toLowerCase() === nameTrimmed.toLowerCase(),
  );
  const canSubmit =
    !!nameTrimmed && !!image && !isDuplicate && (!isCron || !!cron.trim());

  const handleClose = () => {
    if (addMutation.isPending) return;
    setName('');
    setImage(null);
    setCron('');
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    addMutation.mutate(
      {
        appInstanceId,
        payload: {
          Name: nameTrimmed,
          ImageName: image?.Name,
          Cron: isCron ? cron.trim() : undefined,
          EnviromentVariables: {},
        },
      },
      {
        onSuccess: () => {
          handleClose();
          onAdded?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add container</DialogTitle>
      <DialogContent>
        <Box mt={1} mb={3}>
          <TextField
            fullWidth
            label='Container name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={isDuplicate}
            helperText={
              isDuplicate
                ? 'A container with this name already exists on this instance.'
                : ''
            }
          />
        </Box>
        <Box mb={3}>
          <DockerImageSelect
            isMultiple={false}
            label='Container image'
            value={image}
            params={{ AppId: appId }}
            onChange={(e, value) => setImage(value)}
          />
        </Box>
        {isCron && (
          <Box mb={1}>
            <TextField
              fullWidth
              label='Cron expression'
              value={cron}
              onChange={(e) => setCron(e.target.value)}
              placeholder='0 0 * * *'
              helperText='Required for scheduled (job) containers.'
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={addMutation.isPending}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={addMutation.isPending}
        >
          Add
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

AddContainerDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  appId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  appInstanceId: PropTypes.string,
  existingNames: PropTypes.array,
  onAdded: PropTypes.func,
};

export default AddContainerDialog;
